import React, { Suspense, lazy, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import ProtectedRoute from './components/ProtectedRoute';
import PageTransitionLayout from './components/PageTransitionLayout';
import { RoleProvider } from './components/RoleContext';
import { VersionProvider } from './context/VersionContext';
import { VersionFooter } from './components/VersionFooter';
import { UpdateNotification } from './components/UpdateNotification';
import ToastContainer from './components/Toast';
import ErrorBoundary from './components/ErrorBoundary';
import GlobalSearch from './components/GlobalSearch';
import { initMonitoring } from './utils/monitoring';
import { DiveProvider } from './components/DiveContext';
import DiveOverlay from './components/DiveOverlay';

// Inicializa monitoramento (Sentry)
initMonitoring();

// Lazy loading de componentes grandes
const Inicio = lazy(() => import('./components/Inicio'));
const LoginPage = lazy(() => import('./components/LoginPage'));
const RequisicaoCompra = lazy(() => import('./components/RequisicaoCompra'));

function AppContent() {
  const [showGlobalSearch, setShowGlobalSearch] = useState(false);
  const navigate = useNavigate();

  // Atalho Cmd+K / Ctrl+K para busca global
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Cmd+K (Mac) ou Ctrl+K (Windows/Linux)
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowGlobalSearch(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleNavigate = (type, id) => {
    setShowGlobalSearch(false);
    if (type === 'requisicao') navigate('/inicio');
    else if (type === 'poc') navigate('/inicio');
    else if (type === 'fornecedor') navigate('/inicio');
  };

  return (
    <>
      <Suspense fallback={<div style={{position:'fixed',inset:0,background:'#000306'}} />}>
        <Routes>
          <Route element={<PageTransitionLayout />}>
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/inicio" element={<ProtectedRoute><Inicio /></ProtectedRoute>} />
            <Route path="/requisicao" element={<ProtectedRoute><RequisicaoCompra /></ProtectedRoute>} />
            {/* Compras vive DENTRO do /inicio (view interna), não em rota própria. */}
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Route>
        </Routes>
      </Suspense>
      <ToastContainer />

      {/* Indicador de versão (canto inferior esquerdo) */}
      <VersionFooter />

      {/* Notificação de atualização disponível */}
      <UpdateNotification />

      {/* Busca Global Cmd+K */}
      {showGlobalSearch && (
        <GlobalSearch
          onClose={() => setShowGlobalSearch(false)}
          onNavigate={handleNavigate}
        />
      )}
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <VersionProvider>
        <RoleProvider>
          <BrowserRouter>
            <DiveProvider>
              <AppContent />
              <DiveOverlay />
            </DiveProvider>
          </BrowserRouter>
        </RoleProvider>
      </VersionProvider>
    </ErrorBoundary>
  );
}

export default App;
